import { useCallback, useState } from 'react'
import {
  User,
  Shield,
  Crown,
  Wrench,
  Users,
  RefreshCw,
  X,
  Mail,
} from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { DataTable, type DataTableColumn, EmptyState, Badge, cn, AsyncButton } from '@atrvd/saas-ui'
import type { UnifiedTeamMember } from '@/hooks/useTeamMembers'
import { useTablePagination } from '@/hooks/useTablePagination'
import { useResendInvitation, useCancelInvitation } from '@/hooks/useOrganizationInvitations'
import { PRODUCT_ROLE_LABELS } from '@dcp/shared'
import { formatDate } from '@dcp/shared/utils/formatters'
import { queryErrorDisplay, type TableQueryStateProps } from '@/components/common/queryErrorDisplay'

/**
 * Props for TeamMembersTable component.
 * Accepts a unified list of active members and pending/processing/expired invitations.
 */
interface TeamMembersTableProps extends TableQueryStateProps {
  /** Unified list produced by useTeamMembers(), already searched; the table pages it. */
  members: UnifiedTeamMember[]
  /** The owner's search term: changing it returns to page 1; non-empty switches the empty state to "no match". */
  searchTerm: string
  /** ID of the currently authenticated user — used to render the "(you)" indicator */
  currentUserId?: string
}

/**
 * Return display info (label, icon, colour classes) for a system user role.
 * @param role - System user_role string from the DB
 * @returns Object with label, Lucide icon component, and Tailwind colour classes
 */
function getRoleInfo(role: string) {
  switch (role) {
    case 'super_admin':
      return { label: 'Super Admin', icon: Shield, color: 'text-purple-600 bg-purple-50' }
    case 'admin':
      return { label: 'Admin', icon: Shield, color: 'text-blue-600 bg-blue-50' }
    case 'org_owner':
      return { label: 'Owner', icon: Crown, color: 'text-amber-600 bg-amber-50' }
    case 'tech_team':
      return { label: 'Team Member', icon: Wrench, color: 'text-green-600 bg-green-50' }
    case 'user':
      return { label: 'User', icon: User, color: 'text-gray-600 bg-gray-50' }
    case 'viewer':
      return { label: 'Viewer', icon: User, color: 'text-gray-500 bg-gray-50' }
    default:
      return { label: role, icon: User, color: 'text-gray-600 bg-gray-50' }
  }
}

/**
 * Return display info (label, colour classes) for a product role.
 * @param productRole - Product role string or null
 * @returns Object with label and Tailwind colour classes, or null if no role
 */
function getProductRoleInfo(productRole: string | null): { label: string; color: string } | null {
  switch (productRole) {
    // Labels come from PRODUCT_ROLE_LABELS (single source of truth in @dcp/shared).
    // Colour classes remain local because they are a UI concern not shared outside this table.
    case 'accounts_payable':
      return { label: PRODUCT_ROLE_LABELS.accounts_payable, color: 'text-indigo-600 bg-indigo-50' }
    case 'treasury':
      return { label: PRODUCT_ROLE_LABELS.treasury, color: 'text-teal-600 bg-teal-50' }
    case 'view_only':
      return { label: PRODUCT_ROLE_LABELS.view_only, color: 'text-slate-600 bg-slate-50' }
    case 'admin':
      return { label: PRODUCT_ROLE_LABELS.admin, color: 'text-rose-600 bg-rose-50' }
    default:
      return null
  }
}

/**
 * Display info (label, colour classes) for an invitation or member status.
 * @param status - UnifiedTeamMember status value
 * @returns Object with label and Tailwind colour classes
 */
function getStatusInfo(status: UnifiedTeamMember['status']): { label: string; color: string } {
  switch (status) {
    case 'active':
      return { label: 'Active', color: 'text-green-600 bg-green-50' }
    case 'pending':
      return { label: 'Pending', color: 'text-amber-600 bg-amber-50' }
    case 'processing':
      return { label: 'Processing', color: 'text-blue-600 bg-blue-50' }
    case 'expired':
      return { label: 'Expired', color: 'text-gray-500 bg-gray-100' }
  }
}

/**
 * Derive the display name and avatar initial for a unified team member.
 *
 * For active members the full_name is used directly. For invitation rows the
 * name is assembled from firstName/lastName; when both are absent the email
 * is used as the identifier so the row is still meaningful.
 *
 * @param member - Unified team member row
 * @returns { displayName, avatarInitial, emailLine }
 */
function getMemberDisplay(member: UnifiedTeamMember): {
  displayName: string | null
  avatarInitial: string
  emailLine: string
} {
  if (member.kind === 'member') {
    const displayName = member.fullName || null
    const avatarInitial = (member.fullName || member.email || '?').charAt(0).toUpperCase()
    return { displayName, avatarInitial, emailLine: member.email }
  }

  // Invitation row
  const hasName = member.firstName || member.lastName
  const displayName = hasName
    ? [member.firstName, member.lastName].filter(Boolean).join(' ')
    : null
  const avatarInitial = (member.firstName || member.email || '?').charAt(0).toUpperCase()
  return { displayName, avatarInitial, emailLine: member.email }
}

/**
 * Table component displaying unified team members and invitations.
 *
 * Active members show a system role badge, product role badge, joined date,
 * and no action buttons. Invitation rows show pending/processing/expired status,
 * product role, expiry date, and Resend/Cancel action buttons.
 */
export function TeamMembersTable({
  members,
  searchTerm,
  currentUserId,
  loading,
  refreshing,
  error,
  retrying,
  onRetry,
}: TeamMembersTableProps) {
  const pagination = useTablePagination({ totalItems: members.length, resetKey: searchTerm })
  const resendMutation = useResendInvitation()
  const cancelMutation = useCancelInvitation()
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)

  /**
   * Resend an invitation email for the given invitation ID.
   * Guards against concurrent resend calls via local loading state.
   * @param id - Invitation row ID
   */
  const handleResend = useCallback(
    async (id: string) => {
      setResendingId(id)
      try {
        await resendMutation.mutateAsync(id)
      } catch (error) {
        // The global MutationCache.onError only handles auth/session-expired errors, so
        // network or server failures here would produce an unhandled rejection with no
        // user feedback. We swallow intentionally and show a contextual toast instead.
        const email = members.find((m) => m.id === id)?.email
        toast.error(email ? `Failed to resend invitation to ${email}` : 'Failed to resend invitation')
        console.error('[TeamMembersTable] resend failed', error)
      } finally {
        setResendingId(null)
      }
    },
    [resendMutation, members]
  )

  /**
   * Cancel an open or expired invitation by ID.
   * @param id - Invitation row ID
   */
  const handleCancel = useCallback(
    async (id: string) => {
      setCancellingId(id)
      try {
        await cancelMutation.mutateAsync(id)
      } catch (error) {
        // Same rationale as handleResend: global error handler misses non-auth failures.
        // Swallow here so the rejection is handled and the user receives contextual feedback.
        const email = members.find((m) => m.id === id)?.email
        toast.error(email ? `Failed to cancel invitation to ${email}` : 'Failed to cancel invitation')
        console.error('[TeamMembersTable] cancel failed', error)
      } finally {
        setCancellingId(null)
      }
    },
    [cancelMutation, members]
  )

  /**
   * Column definitions for the unified team members table.
   * Columns render differently depending on whether the row is a member or invitation.
   */
  const columns: DataTableColumn<UnifiedTeamMember>[] = [
    {
      key: 'member',
      header: 'Member',
      render: (row) => {
        const { displayName, avatarInitial, emailLine } = getMemberDisplay(row)
        const isInvitation = row.kind === 'invitation'

        return (
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center',
                isInvitation ? 'bg-muted' : 'bg-primary/10'
              )}
            >
              {isInvitation ? (
                <Mail className="h-5 w-5 text-muted-foreground" />
              ) : (
                <span className="text-sm font-medium text-primary">{avatarInitial}</span>
              )}
            </div>
            <div>
              <p className="font-medium">
                {displayName || (isInvitation ? null : 'No name')}
                {row.kind === 'member' && row.id === currentUserId && (
                  <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                )}
              </p>
              <p className="text-sm text-muted-foreground">{emailLine}</p>
            </div>
          </div>
        )
      },
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => {
        if (row.kind === 'invitation') {
          return <span className="text-sm text-muted-foreground">—</span>
        }
        const roleInfo = getRoleInfo(row.role)
        return (
          <Badge className={cn(roleInfo.color)}>
            <roleInfo.icon className="mr-1 h-3.5 w-3.5" />
            {roleInfo.label}
          </Badge>
        )
      },
    },
    {
      key: 'product_role',
      header: 'Product Role',
      render: (row) => {
        const info = getProductRoleInfo(row.productRole)
        if (!info) return <span className="text-sm text-muted-foreground">—</span>
        return <Badge className={cn(info.color)}>{info.label}</Badge>
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const info = getStatusInfo(row.status)
        return (
          <Badge className={cn(info.color)}>
            {info.label}
          </Badge>
        )
      },
    },
    {
      key: 'date',
      header: 'Joined / Sent',
      render: (row) => {
        if (row.kind === 'invitation') {
          return (
            <div>
              <p className="text-sm">{formatDate(row.createdAt)}</p>
              <p
                className={cn(
                  'text-xs',
                  row.status === 'expired' ? 'text-red-500' : 'text-muted-foreground'
                )}
              >
                {row.status === 'expired' ? 'Expired' : 'Expires'} {formatDate(row.expiresAt)}
              </p>
            </div>
          )
        }
        return <span className="text-sm">{formatDate(row.createdAt)}</span>
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => {
        // Active members have no inline actions in this stage
        if (row.kind === 'member') return null

        return (
          <div className="flex items-center gap-2">
            <AsyncButton
              variant="ghost"
              size="sm"
              onClick={() => handleResend(row.id)}
              isLoading={resendingId === row.id}
              title="Resend invitation"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Resend
            </AsyncButton>
            <AsyncButton
              variant="ghost"
              size="sm"
              onClick={() => handleCancel(row.id)}
              isLoading={cancellingId === row.id}
              title="Cancel invitation"
              className="text-destructive hover:text-destructive"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </AsyncButton>
          </div>
        )
      },
    },
  ]

  const { banner, tableError } = queryErrorDisplay({ error, retrying, onRetry }, members.length)

  return (
    <>
      {banner}
      <DataTable
        columns={columns}
        data={pagination.paginate(members)}
        rowKey={(row) => row.id}
        pagination={pagination.dataTablePagination}
        loading={loading}
        refreshing={refreshing}
        error={tableError}
        itemName="members"
        emptyState={
          searchTerm ? (
            <EmptyState
              icon={Users}
              title="No members match your search"
              description="Try a different name or email."
            />
          ) : (
            <EmptyState
              icon={Users}
              title="No team members yet"
              description="Invite team members to collaborate"
            />
          )
        }
      />
    </>
  )
}
